import { useCallback, useMemo, useState } from 'react';
import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import type { MultiPolygon, Polygon } from 'geojson';
import { api } from 'lib/request/api';
import type { SavedDraftGeometry } from 'lib/area-geometries';
import { useGeometryUpload } from 'lib/area-geometry-uploads';
import type { AreaGeometry } from 'types/area-geometry';
import type {
  AreaEditCompare,
  AreaMarkCorrect,
  AreaVerdict,
  AreaVerdictKind,
} from 'types/area-workspace';
import type { WellCounts } from './utils';
import { useAreaVerdict } from './use-workspace-writes';

/** A number per boundary object, handed out as each is first counted. */
const boundaryKeys = new WeakMap<object, number>();
let nextBoundaryKey = 0;

/**
 * The boundary's key for the well count: the same object, the same key.
 *
 * @param {Polygon | MultiPolygon} geometry - The boundary.
 * @return {number} Its key.
 */
const boundaryKey = (geometry: Polygon | MultiPolygon): number => {
  let key = boundaryKeys.get(geometry);
  if (key === undefined) {
    nextBoundaryKey += 1;
    key = nextBoundaryKey;
    boundaryKeys.set(geometry, key);
  }
  return key;
};

/**
 * How many wells intersect a boundary, from the shared wells query. One row is
 * enough: only the count is read.
 *
 * @param {Polygon | MultiPolygon} geometry - The boundary.
 * @return {object} The query options.
 */
const wellCountQuery = (geometry: Polygon | MultiPolygon) =>
  queryOptions({
    // Keyed on the boundary object, not its coordinates: hashing an uploaded
    // shape's hundreds of thousands of vertices on every render costs the main
    // thread. A save's "before" is the last save's "after", so it still hits.
    queryKey: ['area-workspace', 'well-count', boundaryKey(geometry)],
    queryFn: async ({ signal }) => {
      const body = await api
        .post('queries/wells', {
          json: {
            query: {
              polygon_geojson: geometry,
              page: 1,
              pageSize: 1,
              use_elastic: true,
            },
          },
          signal,
        })
        .json<{ totalCount?: number }>();
      return body.totalCount ?? 0;
    },
    staleTime: 60_000,
    retry: false,
  });

/**
 * Counts the wells inside the old and the new boundary, for the save toast.
 *
 * @param {Polygon | MultiPolygon} before - The boundary before the save.
 * @param {Polygon | MultiPolygon} after - The boundary saved.
 * @return {object} Both counts once they land (null otherwise), and whether the
 * asking is over, landed or failed.
 */
export function useWellCounts(
  before: Polygon | MultiPolygon,
  after: Polygon | MultiPolygon
): { counts: WellCounts | null; settled: boolean } {
  const [old, next] = useQueries({
    queries: [wellCountQuery(before), wellCountQuery(after)],
  });
  const counts =
    old.data !== undefined && next.data !== undefined
      ? { before: old.data, after: next.data }
      : null;
  return { counts, settled: counts !== null || old.isError || next.isError };
}

/** One geometry save, for its toast. */
export interface GeometrySave {
  /** Counts saves, so each one gets a fresh toast. */
  id: number;
  before: Polygon | MultiPolygon;
  after: Polygon | MultiPolygon;
}

/**
 * The workspace's geometry saves. The last saved shape and its Gross Acres stand
 * in for the area's until the area read carries them: a read newer than the one
 * the save was made on (after a refresh, or another tab's save) takes over. Each
 * save is kept for the toast that compares the wells either side.
 *
 * @param {AreaGeometry} area - The area as last read.
 * @return {object} The area to show, the save to report, and the two handlers.
 */
export function useGeometryDraft(area: AreaGeometry) {
  const [draft, setDraft] = useState<{
    saved: SavedDraftGeometry;
    /** The area read the save was made over. */
    readAt: string;
  } | null>(null);
  const current = draft?.readAt === area.updatedAt ? draft.saved : null;
  const [lastSave, setLastSave] = useState<GeometrySave | null>(null);

  const shownArea = useMemo<AreaGeometry>(
    () =>
      current
        ? {
            ...area,
            geometry: current.geometry,
            grossAcres: current.grossAcres,
            parts: current.parts,
          }
        : area,
    [area, current]
  );

  const recordSave = useCallback(
    (saved: SavedDraftGeometry) => {
      setLastSave((last) => ({
        id: (last?.id ?? 0) + 1,
        before: shownArea.geometry,
        after: saved.geometry,
      }));
      setDraft({ saved, readAt: area.updatedAt });
    },
    [shownArea.geometry, area.updatedAt]
  );

  const dismissSave = useCallback(() => setLastSave(null), []);

  return { shownArea, lastSave, recordSave, dismissSave };
}

/**
 * The draft against the published area, read only while Compare is open: nothing
 * else needs it, and each opening reads it afresh since the draft may have moved.
 *
 * @param {number} areaGeometryId - The workspace's draft.
 * @param {boolean} enabled - Whether Compare is open.
 * @return {object} The query.
 */
export const useAreaEditCompare = (areaGeometryId: number, enabled: boolean) =>
  useQuery(
    queryOptions({
      queryKey: ['area-workspace', 'edit-compare', areaGeometryId],
      queryFn: ({ signal }) =>
        api
          .get(`area_geometries/${areaGeometryId}/edit_compare`, { signal })
          .json<AreaEditCompare>(),
      enabled,
      // Dropped once the sheet closes, so a reopening waits on a fresh read rather
      // than showing the last one's changes while it refetches.
      gcTime: 0,
      retry: false,
    })
  );

/**
 * The workspace bar's Mark as Correct / Incorrect pair and the panel behind it.
 *
 * The pair shows only when the workspace has a freeze item to mark, and is held once the
 * workspace may no longer set one (`auth` loses `create` on promote). Clicking the
 * verdict the area already carries clears it; the other one opens the panel for a reason.
 * @param {AreaMarkCorrect | undefined} markCorrect The workspace's `mark_correct` block.
 * @param {() => void} onChanged Re-reads the workspace once a verdict lands.
 * @return {object} Props for the bar's pair and for the panel.
 */
export const useAreaVerdictControls = (
  markCorrect: AreaMarkCorrect | undefined,
  onChanged: () => void
) => {
  const [kind, setKind] = useState<AreaVerdictKind | null>(null);
  const setter = useAreaVerdict(markCorrect?.eventsHref, onChanged);

  const verdict: AreaVerdict | null | undefined =
    markCorrect?.verificationItemId ? (markCorrect.state ?? null) : undefined;

  return {
    header: {
      verdict,
      // No events link means nowhere to post: the setter would report a verdict it
      // never sent.
      verdictDisabled:
        !markCorrect?.eventsHref ||
        !markCorrect.auth?.includes('create') ||
        setter.saving,
      onSetVerdict: (next: AreaVerdictKind) => {
        setter.reset();
        setKind(next);
      },
      onClearVerdict: () => setter.set('unanswered'),
    },
    panel: {
      kind,
      setter,
      // A refusal the panel already showed is dropped with it, so closing the
      // panel doesn't raise it again as the clear's toast.
      onClose: () => {
        setter.reset();
        setKind(null);
      },
    },
  };
};

/**
 * Whether Edit Area Geometry is open, and the files its upload method holds. Each
 * open starts without files, as it starts from the area as it is, and a close
 * drops them, which stops their status reads and channels.
 *
 * @return {object} The open state, the upload, and the open and close actions.
 */
export function useGeometryEditor() {
  const [editing, setEditing] = useState(false);
  const upload = useGeometryUpload();
  const { set: setUpload } = upload;

  const open = useCallback(() => {
    setUpload([]);
    setEditing(true);
  }, [setUpload]);
  const close = useCallback(() => {
    setEditing(false);
    setUpload([]);
  }, [setUpload]);

  return { editing, upload, open, close };
}
